import React, { useState } from "react";
import {
  ProductCard,
  ProductCardProduct,
  ProductCardProps,
} from "./ProductCard";
import {
  SubscriptionSelector,
  SubscriptionSelectorValue,
} from "./SubscriptionSelector";
import { Button } from "@/components/Button";
import { shopifyCartApi } from "@/services/shopifyCart";
import { IconButton, Modal } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import {
  DroppsProduct,
  DroppsVariant,
  productSelect,
  variantSelect,
} from "@/store";
import { useCartUiActions } from "../cart";
import { cn } from "@/helpers/utils";
import InfoIcon from "@mui/icons-material/Info";

export interface ShoppableProductCardProduct extends ProductCardProduct {
  product: DroppsProduct;
  variant: DroppsVariant;
  collectionProducts: DroppsProduct[];
}

export interface ShoppableProductCardProps
  extends Omit<ProductCardProps, "productCardValue"> {
  product: ShoppableProductCardProduct;
}

export const ShoppableProductCard = ({
  product,
  className,
  showRatings,
  ...props
}: ShoppableProductCardProps) => {
  const isPLP = window.location.href.includes("/collections/");
  const showSubSelector = variantSelect.orderableSubscription(product.variant);
  const addItemsToCart = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();
  const [showModal, setShowModal] = useState(false);
  const [subToggle, setSubToggle] = useState<SubscriptionSelectorValue>(
    showSubSelector ? "subscription" : "one-time"
  );

  const handleModalClose = () => {
    setShowModal(false);
  };

  const unavailable = !productSelect.orderable(product.product, {
    bulk: "include",
  });

  const productCardProduct =
    subToggle == "subscription"
      ? {
          ...product,
          price: variantSelect.lowestPrice(product.variant),
        }
      : product;

  const subDiscount = variantSelect.subscriptionDiscount(product.variant);
  const shopActions = (
    <div className="space-y-4">
      {showSubSelector && (
        <SubscriptionSelector
          value={subToggle}
          onChange={(value) => {
            setSubToggle(value);
          }}
          size="sm"
          cta={
            <p className="body w-full text-center text-xs">
              {showSubSelector.discount.kind === "percentage" &&
              showSubSelector.discount.percentage > 0 ? (
                <span className="whitespace-nowrap">
                  Save {showSubSelector.discount.percentage}% when you
                  subscribe.
                </span>
              ) : null}{" "}
              <span className="whitespace-nowrap">
                Change or cancel anytime.
              </span>{" "}
              <span className="whitespace-nowrap">Ships every 4 months.</span>
            </p>
          }
        />
      )}
      <div className="flex w-full items-center justify-between space-x-2 lg:flex-col lg:space-x-0 lg:space-y-2 xl:flex-row xl:space-x-2 xl:space-y-0">
        {typeof product.url === "string" ? (
          <Button
            className="items-center space-x-2"
            component="a"
            size="sm"
            variant="ghost"
            href={product.url}
            aria-label={`Learn more about ${product.title}`}
          >
            <InfoIcon className="h-4 w-4 text-inherit" />{" "}
            <span>More details</span>
          </Button>
        ) : null}
        <Button
          className="lg:btn-sm xl:btn-base"
          onClick={() => {
            const lineItem = variantSelect.cartAddLineItem(product.variant, {
              subscription:
                subToggle === "subscription"
                  ? productSelect.defaultSellingPlan(product.product)
                  : false,
            });

            const isBundle =
              product?.product?.tags?.includes("bundle") &&
              !product.product.handle.includes("starter-kit");

            const body = isBundle
              ? {
                  items: [lineItem],
                  sourceModule: "bundle_plp_quick_add",
                }
              : { items: [lineItem], sourceModule: "plp_quick_add" };
            addItemsToCart.mutate(body, {
              onSuccess: () => {
                cartActions.openDrawer();
                handleModalClose();
              },
            });
          }}
          disabled={unavailable}
          loading={addItemsToCart.isLoading}
          aria-label={`Add ${product.title} to cart`}
          data-id={
            subToggle === "subscription"
              ? "add-to-cart-subscription"
              : "add-to-cart-spot"
          }
        >
          {unavailable ? "Unavailable" : "+ Add"}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <div className={cn("overflow-hidden rounded-lg lg:hidden", className)}>
        <ProductCard
          {...props}
          productCardValue={productCardProduct}
          hoverable={false}
          bordered={false}
          rounded={false}
          showRatings={showRatings}
          cardContentClass="py-4 px-2 sm:px-4"
        />

        {isPLP ? (
          <div className="px-2">
            <div className="relative border-0 border-b border-solid border-primary bg-primary-100 p-2 leading-none">
              <Button
                variant="text"
                size="sm"
                onClick={() => setShowModal(true)}
                className="leading-none after:absolute after:bottom-0 after:left-0 after:top-0 after:h-full after:w-full after:content-['']"
              >
                Quick Add +
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-b-lg bg-white px-4 pb-4">
            <Button variant="text" size="sm" onClick={() => setShowModal(true)}>
              Quick Add +
            </Button>
          </div>
        )}
        <Modal
          className="mx-auto max-w-md overflow-auto sm:mt-5"
          aria-label={`quick add ${productSelect.name(
            product.product
          )} to cart modal`}
          aria-modal={true}
          open={showModal}
          onClose={handleModalClose}
          role="dialog"
        >
          <div>
            <ProductCard
              {...props}
              productCardValue={{
                ...productCardProduct,
                badge: {
                  text: productCardProduct.badge?.text,
                  image: productCardProduct.badge?.image,
                  position: productCardProduct.badge?.position,
                  width: productCardProduct.badge?.width,
                },
                url: undefined,
              }}
              productTitle={
                <h3 data-id="product-card-title" className="title text-lg">
                  {productSelect.name(product.product)}
                </h3>
              }
              hoverable={false}
              bordered={false}
              rounded={false}
              actions={shopActions}
              cover={false}
            />
            <IconButton
              className="right-0 top-0 z-50 bg-white"
              size="large"
              onClick={handleModalClose}
              tabIndex={0}
              style={{
                outline: "revert",
                position: "absolute",
                borderRadius: 0,
              }}
              aria-label="close modal"
              color="inherit"
            >
              <ClearIcon className="transition hover:opacity-50" />
            </IconButton>
          </div>
        </Modal>
      </div>
      <ProductCard
        {...props}
        className="hidden lg:block"
        productCardValue={productCardProduct}
        hoverable={"expand"}
        actions={shopActions}
        rounded={true}
        showRatings={showRatings}
      />
    </>
  );
};
